import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Sparkles, Loader2, Play, Pencil, Home, Wand2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { key: 'design', label: 'Designing the game' },
  { key: 'combat_detection', label: 'Detecting combat & tone' },
  { key: 'levels', label: 'Synthesizing reachable levels' },
  { key: 'assets', label: 'Generating original art' },
  { key: 'placement', label: 'Placing entities' },
  { key: 'cover_art', label: 'Painting cover art' },
  { key: 'done', label: 'Ready to play' },
];

const SelectField = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="text-[10px] text-zinc-500 uppercase font-mono block mb-1">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="w-full bg-[#0F0F12] border border-[#1E1E24] rounded-md px-2 py-1.5 text-sm text-white">
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export default function Generator() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [prompt, setPrompt] = useState('');
  const [dimension, setDimension] = useState('2d');
  const [refImage, setRefImage] = useState(null);
  const [refUploading, setRefUploading] = useState(false);

  const uploadRefImage = async (file) => {
    if (!file || refUploading) return;
    setRefUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', 'image');
      const r = await axios.post(`${API}/uploads`, fd, { withCredentials: true });
      if (r.data?.upload?.url) {
        setRefImage(r.data.upload.url);
        toast.success('Hero reference attached — the generated hero will use this art');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reference upload failed');
    } finally { setRefUploading(false); }
  };
  const [genre, setGenre] = useState('auto');
  const [artStyle, setArtStyle] = useState('auto');
  const [stages, setStages] = useState(3);
  const [lives, setLives] = useState(3);
  const [job, setJob] = useState(null);
  const [clarify, setClarify] = useState(null);
  const [clarifyLoading, setClarifyLoading] = useState(false);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatBrief, setChatBrief] = useState('');

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    const next = [...chatMsgs, { role: 'user', content: text }];
    setChatMsgs(next);
    setChatInput('');
    setChatBusy(true);
    try {
      const r = await axios.post(`${API}/gamegen/chat`, { message: text, history: chatMsgs }, { withCredentials: true, timeout: 60000 });
      setChatMsgs([...next, { role: 'assistant', content: r.data.reply }]);
      if (r.data.ready && r.data.brief) setChatBrief(r.data.brief);
    } catch {
      setChatMsgs([...next, { role: 'assistant', content: 'Designer unavailable — try again in a moment.' }]);
    } finally {
      setChatBusy(false);
    }
  };
  const [covers, setCovers] = useState([]);
  const [selectedCover, setSelectedCover] = useState(null);
  const [moreLoading, setMoreLoading] = useState(false);
  const [gameName, setGameName] = useState('');
  const [nameChoice, setNameChoice] = useState(null);
  const pollRef = useRef(null);

  const chooseName = async (n) => {
    try {
      await axios.put(`${API}/projects/${job.project_id}`, { name: n }, { withCredentials: true });
      setNameChoice(n);
      toast.success(`Game named "${n}"`);
    } catch { toast.error('Rename failed'); }
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  const startPolling = (jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await axios.get(`${API}/gamegen/status/${jobId}`, { withCredentials: true });
        setJob(s.data);
        if (s.data.status === 'completed') {
          clearInterval(pollRef.current);
          setCovers(s.data.cover_options || []);
          if ((s.data.cover_options || [])[0]) setSelectedCover(s.data.cover_options[0]);
          toast.success(`"${s.data.title}" is ready to play!`);
        } else if (s.data.status === 'failed') {
          clearInterval(pollRef.current);
          toast.error('Generation failed: ' + (s.data.error || 'unknown'));
        }
      } catch { /* keep polling */ }
    }, 2500);
  };

  // Resume a job handed off from another page (e.g. gallery AI Remix)
  useEffect(() => {
    const jobId = searchParams.get('job');
    const projectId = searchParams.get('project');
    if (jobId && projectId && !job) {
      setJob({ job_id: jobId, project_id: projectId, status: 'running', stage: 'design', progress: 5 });
      startPolling(jobId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const startGeneration = async (finalPrompt) => {
    try {
      const res = await axios.post(`${API}/gamegen/start`, {
        prompt: finalPrompt ?? prompt, dimension, genre, art_style: artStyle, stages: Number(stages), lives: Number(lives),
        ...(gameName.trim() ? { name: gameName.trim() } : {}),
        ...(refImage ? { reference_image_url: refImage } : {}),
      }, { withCredentials: true });
      setNameChoice(null);
      setClarify(null);
      setJob({ job_id: res.data.job_id, project_id: res.data.project_id, status: 'running', stage: 'design', progress: 5 });
      startPolling(res.data.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start generation');
    }
  };

  // Converse first: the designer asks clarifying questions when the idea leaves things open
  const onGenerateClick = async () => {
    if (!clarify) {
      setClarifyLoading(true);
      try {
        const r = await axios.post(`${API}/gamegen/clarify`, { prompt }, { withCredentials: true, timeout: 30000 });
        const qs = r.data?.questions || [];
        if (qs.length) {
          setClarify({ questions: qs, answers: qs.map(() => '') });
          setClarifyLoading(false);
          return;
        }
      } catch { /* clarify unavailable → design from scratch anyway */ }
      setClarifyLoading(false);
    }
    startGeneration();
  };

  const generateMoreCovers = async () => {
    setMoreLoading(true);
    try {
      const r = await axios.post(`${API}/gamegen/more-covers`, { project_id: job.project_id }, { withCredentials: true, timeout: 90000 });
      setCovers(prev => [...prev, ...(r.data.covers || [])]);
      toast.success(`${(r.data.covers || []).length} new covers generated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cover generation failed — try again');
    } finally {
      setMoreLoading(false);
    }
  };

  const pickCover = async (url) => {
    setSelectedCover(url);
    try {
      await axios.post(`${API}/gamegen/select-cover`, { project_id: job.project_id, cover_url: url }, { withCredentials: true });
    } catch { /* non-fatal */ }
  };

  const running = job && job.status !== 'completed' && job.status !== 'failed';
  const doneIdx = job ? STAGES.findIndex((s) => s.key === job.stage) : -1;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="generator-page">
      <header className="border-b border-[#1E1E24] px-6 py-4 flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="text-zinc-400 hover:text-white" data-testid="generator-home-btn">
          <Home className="w-5 h-5" />
        </Button>
        <h1 className="text-xl font-bold flex items-center gap-2"><Wand2 className="w-5 h-5 text-[#06B6D4]" /> AI Game Generator</h1>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        {!job && (
          <div className="space-y-6">
            <div>
              <label className="text-sm text-zinc-400 block mb-2">Describe your game idea (or leave blank — we'll surprise you)</label>
              <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
                placeholder="A firefly shepherdess guiding lost light-motes home through a storm-flooded lantern city…"
                className="bg-[#0F0F12] border-[#1E1E24] text-white" data-testid="generator-prompt-input" />
            </div>
            <div>
              <label className="text-sm text-zinc-400 block mb-2">Game name <span className="text-zinc-600">(optional — the studio will suggest one too, and you pick)</span></label>
              <input value={gameName} onChange={(e) => setGameName(e.target.value)} maxLength={60}
                placeholder="Leave blank to let the studio name it"
                className="w-full bg-[#0F0F12] border border-[#1E1E24] rounded-md px-3 h-10 text-sm text-white focus:outline-none focus:border-[#06B6D4]"
                data-testid="generator-name-input" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <SelectField label="Dimension" value={dimension} onChange={setDimension} options={['2d', '3d']} testId="generator-dimension" />
              <SelectField label="Genre" value={genre} onChange={setGenre} options={['auto', 'platformer', 'shooter', 'puzzle', 'adventure', 'survival']} testId="generator-genre" />
              <SelectField label="Art style" value={artStyle} onChange={setArtStyle} options={['auto', 'pixel art', 'painterly', 'toon', 'neon synthwave', 'watercolor']} testId="generator-artstyle" />
              <SelectField label="Stages" value={stages} onChange={setStages} options={[1, 2, 3, 4, 5]} testId="generator-stages" />
              <SelectField label="Lives" value={lives} onChange={setLives} options={[1, 2, 3, 5]} testId="generator-lives" />
            </div>
            {/* Hero reference image — uploaded art becomes the playable hero's sprite */}
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer border border-dashed border-[#1E1E24] hover:border-[#06B6D4] hover:text-white rounded-md px-3 py-2 transition-colors">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  disabled={refUploading}
                  onChange={(e) => uploadRefImage(e.target.files?.[0])}
                  data-testid="generator-ref-input"
                />
                {refUploading ? 'Uploading…' : refImage ? '↺ Replace hero reference' : '📎 Attach hero reference image (optional)'}
              </label>
              {refImage && (
                <div className="flex items-center gap-2">
                  <img src={refImage} alt="hero reference" className="w-10 h-10 rounded object-cover border border-[#06B6D4]/40" data-testid="generator-ref-thumb" />
                  <button onClick={() => setRefImage(null)} className="text-zinc-500 hover:text-red-400 text-xs" data-testid="generator-ref-clear">✕ remove</button>
                </div>
              )}
              <p className="w-full text-[10px] text-zinc-600 -mt-1">Your image becomes the playable hero's in-game art.</p>
            </div>
            {clarify && (
              <div className="space-y-3 border border-[#06B6D4]/30 rounded-md p-4 bg-[#0B0F12]" data-testid="generator-clarify">
                <p className="text-sm text-[#67E8F9] font-semibold">Before I design this from scratch — a few quick questions:</p>
                {clarify.questions.map((q, i) => (
                  <div key={i}>
                    <label className="text-xs text-zinc-400 block mb-1">{q}</label>
                    <Textarea rows={1} value={clarify.answers[i]}
                      onChange={(e) => setClarify(c => ({ ...c, answers: c.answers.map((a, j) => j === i ? e.target.value : a) }))}
                      className="bg-[#0F0F12] border-[#1E1E24] text-white min-h-[36px]" data-testid={`generator-clarify-answer-${i}`} />
                  </div>
                ))}
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      const detail = clarify.questions.map((q, i) => clarify.answers[i]?.trim() ? `${q} → ${clarify.answers[i].trim()}` : null).filter(Boolean).join(' | ');
                      startGeneration(detail ? `${prompt}\nDesign answers: ${detail}` : prompt);
                    }}
                    className="bg-[#06B6D4] text-black font-bold" data-testid="generator-clarify-continue">
                    Answer & Generate
                  </Button>
                  <Button variant="outline" onClick={() => startGeneration()} className="border-[#1E1E24] text-zinc-300" data-testid="generator-clarify-skip">
                    Skip — you decide
                  </Button>
                </div>
              </div>
            )}
            {!clarify && (
              <Button onClick={onGenerateClick} disabled={clarifyLoading} className="w-full bg-[#06B6D4] text-black font-bold py-6 text-lg hover:bg-[#0891B2]" data-testid="generator-start-btn">
                {clarifyLoading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Sparkles className="w-5 h-5 mr-2" />} Generate Game
              </Button>
            )}

            {/* Design by Chat — converse with the AI designer, then build exactly that game */}
            <div className="border border-[#1E1E24] rounded-md p-4 space-y-3 bg-[#0B0B0E]" data-testid="generator-chat">
              <p className="text-sm font-semibold text-[#67E8F9]">Design by Chat</p>
              {chatMsgs.length > 0 && (
                <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
                  {chatMsgs.map((m, i) => (
                    <div key={i} className={`text-xs rounded px-2.5 py-1.5 whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#06B6D4]/15 text-white ml-8' : 'bg-white/5 text-zinc-300 mr-8'}`}>
                      {m.content}
                    </div>
                  ))}
                  {chatBusy && <p className="text-[10px] text-zinc-500 animate-pulse">designing…</p>}
                </div>
              )}
              {chatBrief && (
                <div className="space-y-2">
                  <p className="text-[11px] text-zinc-400 border border-[#22C55E]/30 rounded p-2 bg-[#22C55E]/5">{chatBrief}</p>
                  <Button onClick={() => { setPrompt(chatBrief); startGeneration(chatBrief); }} className="w-full bg-[#22C55E] text-black font-bold" data-testid="generator-chat-generate">
                    Generate this game
                  </Button>
                </div>
              )}
              <div className="flex gap-2">
                <Textarea rows={1} value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                  placeholder="Chat your game idea — the designer asks questions, then builds exactly that"
                  className="bg-[#0F0F12] border-[#1E1E24] text-white min-h-[38px] flex-1" data-testid="generator-chat-input" />
                <Button onClick={sendChat} disabled={chatBusy || !chatInput.trim()} className="bg-[#06B6D4] text-black self-end" data-testid="generator-chat-send">
                  Send
                </Button>
              </div>
            </div>
            <p className="text-xs text-zinc-600">Every generated game is instantly playable, winnable, and themed — no placeholders.</p>
          </div>
        )}

        {job && (
          <div className="space-y-6">
            <div className="space-y-3" data-testid="generator-progress">
              {STAGES.map((s, i) => {
                const state = job.status === 'failed' ? (i < doneIdx ? 'done' : 'idle')
                  : i < doneIdx || job.status === 'completed' ? 'done'
                  : i === doneIdx ? 'active' : 'idle';
                return (
                  <div key={s.key} className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${state === 'active' ? 'border-[#06B6D4]/50 bg-[#06B6D4]/5' : 'border-[#1E1E24] bg-[#0A0A0C]'}`}>
                    {state === 'done' ? <CheckCircle2 className="w-4 h-4 text-green-400" />
                      : state === 'active' ? <Loader2 className="w-4 h-4 text-[#06B6D4] animate-spin" />
                      : <div className="w-4 h-4 rounded-full border border-zinc-700" />}
                    <span className={state === 'idle' ? 'text-zinc-600 text-sm' : 'text-sm'}>{s.label}</span>
                  </div>
                );
              })}
            </div>

            {job.status === 'failed' && (
              <div className="text-red-400 text-sm" data-testid="generator-error">
                {job.error || 'Generation failed.'} <Button variant="link" className="text-[#06B6D4]" onClick={() => setJob(null)}>Try again</Button>
              </div>
            )}

            {job.status === 'completed' && (
              <div className="space-y-6" data-testid="generator-result">
                <h2 className="text-lg font-bold">{nameChoice || job.title}</h2>
                {(() => {
                  const current = nameChoice || job.title;
                  const opts = (job.suggested_names?.length ? job.suggested_names : [job.suggested_name]).filter(n => n && n !== current);
                  return !nameChoice && opts.length > 0 && (
                    <div className="bg-[#06B6D4]/5 border border-[#06B6D4]/30 rounded-lg p-4 space-y-3" data-testid="generator-name-chooser">
                      <p className="text-sm text-zinc-300">The studio pitched {opts.length > 1 ? 'a few names' : 'a name'} too — pick your favourite:</p>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" onClick={() => chooseName(current)} variant="outline"
                          className="bg-transparent border-[#1E1E24] text-zinc-300 hover:text-white text-xs"
                          data-testid="generator-keep-my-name">Keep “{current}”</Button>
                        {opts.map((n, i) => (
                          <Button key={n} size="sm" onClick={() => chooseName(n)}
                            className="bg-[#06B6D4] text-black hover:bg-[#0891B2] text-xs"
                            data-testid={i === 0 ? 'generator-use-suggested-name' : `generator-use-suggested-name-${i}`}>“{n}”</Button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                {(job.theme || job.core_loop) && (
                  <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-lg p-4" data-testid="generator-narrative">
                    {job.theme && <p className="text-sm text-[#06B6D4] mb-1">{job.theme}</p>}
                    {job.core_loop && <p className="text-xs text-zinc-400">{job.core_loop}</p>}
                  </div>
                )}
                {covers.length > 0 && (
                  <div>
                    <p className="text-sm text-zinc-400 mb-2">Pick your cover art</p>
                    <div className="grid grid-cols-3 gap-3">
                      {covers.map((c, i) => (
                        <button key={i} onClick={() => pickCover(c)} data-testid={`generator-cover-${i}`}
                          className={`rounded-lg overflow-hidden border-2 transition-all ${selectedCover === c ? 'border-[#06B6D4] scale-[1.02]' : 'border-transparent opacity-70 hover:opacity-100'}`}>
                          <img src={c} alt={`Cover ${i + 1}`} className="w-full aspect-square object-cover" />
                        </button>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={generateMoreCovers}
                      disabled={moreLoading}
                      className="mt-3 border-[#1E1E24] text-zinc-300 text-xs"
                      data-testid="generator-more-covers-btn"
                    >
                      {moreLoading ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Sparkles className="w-3 h-3 mr-2" />}
                      {moreLoading ? 'Painting new covers…' : 'Generate more covers'}
                    </Button>
                  </div>
                )}
                <div className="flex gap-3">
                  {searchParams.get('job') ? (
                    <>
                      {/* Remixes open in the EDITOR first — remixing is for editing */}
                      <Button onClick={() => navigate(`/editor/${job.project_id}`)} className="flex-1 bg-[#06B6D4] text-black font-bold" data-testid="generator-edit-btn">
                        Edit Your Remix
                      </Button>
                      <Button variant="outline" onClick={() => navigate(`/play/${job.project_id}`)} className="flex-1 border-[#1E1E24]" data-testid="generator-play-btn">
                        <Play className="w-4 h-4 mr-2" /> Play Now
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={() => navigate(`/play/${job.project_id}`)} className="flex-1 bg-[#06B6D4] text-black font-bold" data-testid="generator-play-btn">
                        <Play className="w-4 h-4 mr-2" /> Play Now
                      </Button>
                      <Button variant="outline" onClick={() => navigate(`/editor/${job.project_id}`)} className="flex-1 border-[#1E1E24]" data-testid="generator-edit-btn">
                        <Pencil className="w-4 h-4 mr-2" /> Open in Editor
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}

            {running && <p className="text-xs text-zinc-500 text-center">Generating original art & levels — usually 1–3 minutes…</p>}
          </div>
        )}
      </div>
    </div>
  );
}
